import {Action, ActionReducer, createReducer, on} from '@ngrx/store';
import {getDeepCopyOfObject} from 'src/app/shared/shared-utils/object/object.utils';
import {
  IAdvancedFilterGroup,
  IFilterGroup,
  migrateAdvancedFilterGroupStructure,
  normalizeAdvancedFilterGroup,
} from '../../settings-new/models';
import * as SharedActions from './shared-filter.actions';
import {
  deleteFilterOrGroupWithinAdvancedFilterGroup,
  findAndReplaceFilterWithinAdvancedFilterGroup,
  onActionAddFilterAndGroupToSelectedAdvancedFilterGroup,
  onActionAddFilterToSelectedAdvancedFilterGroup,
  onActionAddGroupToSelectedAdvancedFilterGroup,
  onCreateAdvancedFilterGroup,
  onGenerateCopyOfAdvancedFilterGroup,
  onGenerateNewAdvancedFilterGroup,
  onSetSelectedAdvancedFilterGroupOperator,
} from './shared-filter.reducer.utils';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {matchesAdvancedFilterGridCategory} from '../../settings-new/models/general/advanced-filter-grid.constants';

export interface SharedFilterState {
  advancedFilterGroups: IAdvancedFilterGroup[];
  advancedFilterIDCounter: number;
  appliedAdvancedFilterGroup: IAdvancedFilterGroup;
  errors: IGlobalError[];
  isAdvancedFilterGroupLoading: boolean;
  selectedAdvancedFilterGroup: IAdvancedFilterGroup;
  selectedAdvancedFilterGroupGrid: string;
}

export const initialSharedFilterState: SharedFilterState = {
  advancedFilterIDCounter: 0,
  advancedFilterGroups: [],
  appliedAdvancedFilterGroup: null,
  errors: [],
  isAdvancedFilterGroupLoading: false,
  selectedAdvancedFilterGroup: null,
  selectedAdvancedFilterGroupGrid: '',
};

const createSharedFilterReducer: ActionReducer<SharedFilterState> = createReducer(
  initialSharedFilterState,
  // ====================================================================================================
  // Create Advanced Filter Group
  // ====================================================================================================
  on(
    SharedActions.addFilterAndGroupToSelectedAdvancedFilterGroup,
    (state: SharedFilterState, {predecessorID, shouldAddFilter}) =>
      onActionAddFilterAndGroupToSelectedAdvancedFilterGroup(state, predecessorID, shouldAddFilter),
  ),
  on(
    SharedActions.addFilterToSelectedAdvancedFilterGroup,
    (state: SharedFilterState, {predecessorID}) =>
      onActionAddFilterToSelectedAdvancedFilterGroup(state, predecessorID),
  ),
  on(
    SharedActions.addGroupToSelectedAdvancedFilterGroup,
    (state: SharedFilterState, {predecessorID}) =>
      onActionAddGroupToSelectedAdvancedFilterGroup(state, predecessorID),
  ),
  on(
    SharedActions.applyAdvancedFilterGroupFilterToGrid,
    (state: SharedFilterState, {advancedFilterGroup}) => {
      const sourceGroup = advancedFilterGroup ?? state.selectedAdvancedFilterGroup ?? null;
      const appliedAdvancedFilterGroup = sourceGroup
        ? normalizeAdvancedFilterGroup(sourceGroup)
        : null;
      return {
        ...state,
        appliedAdvancedFilterGroup,
      };
    },
  ),
  on(
    SharedActions.discardUnsavedAdvancedFilterGroup,
    (state: SharedFilterState) => {
      const selected = state.selectedAdvancedFilterGroup;
      if (!selected || selected.saved) {
        return state;
      }
      const advancedFilterGroups = state.advancedFilterGroups.filter(
        (group: IAdvancedFilterGroup): boolean => group.id !== selected.id,
      );
      const appliedAdvancedFilterGroup = state.appliedAdvancedFilterGroup?.id === selected.id
        ? undefined
        : state.appliedAdvancedFilterGroup;
      return {
        ...state,
        advancedFilterGroups,
        appliedAdvancedFilterGroup,
        selectedAdvancedFilterGroup: null,
      };
    },
  ),
  on(
    SharedActions.cancelEditingOfAdvancedFilterGroup,
    (state: SharedFilterState, {originalAdvancedFilterGroup}) => {
      const original = getDeepCopyOfObject(originalAdvancedFilterGroup);
      original.saved = true;
      const advancedFilterGroups = getDeepCopyOfObject<IAdvancedFilterGroup[]>(state.advancedFilterGroups);
      const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === original.id);
      if (index === -1) {
        return state;
      }
      advancedFilterGroups[index] = original;
      return {
        ...state,
        advancedFilterGroups,
        selectedAdvancedFilterGroup: getDeepCopyOfObject(original),
      };
    },
  ),
  on(
    SharedActions.removeAdvancedFilterGroupFilterFromGrid,
    SharedActions.clearAppliedAdvancedFilterGroup,
    (state: SharedFilterState) => {
      return {
        ...state,
        appliedAdvancedFilterGroup: undefined,
      };
    },
  ),
  on(
    SharedActions.cloneSelectedAdvancedFilterGroup,
    (state: SharedFilterState) => {
      const copy = onGenerateCopyOfAdvancedFilterGroup(state.selectedAdvancedFilterGroup, state.advancedFilterIDCounter);
      const advancedFilterGroups = [...state.advancedFilterGroups];
      advancedFilterGroups.unshift(copy);
      return {
        ...state,
        advancedFilterIDCounter: state.advancedFilterGroups.length + 1,
        advancedFilterGroups,
        selectedAdvancedFilterGroup: copy,
      };
    },
  ),
  on(
    SharedActions.createAdvancedFilterGroup,
    (state: SharedFilterState, {grid}) => {
      const newAdvancedFilterGroup = onGenerateNewAdvancedFilterGroup(state.advancedFilterGroups.length, state.advancedFilterIDCounter, grid);
      const advancedFilterGroups = onCreateAdvancedFilterGroup(state.advancedFilterGroups, newAdvancedFilterGroup);
      return {
        ...state,
        advancedFilterIDCounter: state.advancedFilterIDCounter + 4,
        advancedFilterGroups,
        selectedAdvancedFilterGroup: newAdvancedFilterGroup,
      };
    },
  ),
  on(
    SharedActions.clearAdvancedFilterGroupsFromGrid,
    (state: SharedFilterState) => {
      return {
        ...state,
        appliedAdvancedFilterGroup: undefined,
      };
    },
  ),
  on(
    SharedActions.deleteSelectedAdvancedFilterGroup,
    (state: SharedFilterState) => {
      const appliedAdvancedFilterGroup =
        state.appliedAdvancedFilterGroup && state.selectedAdvancedFilterGroup.id === state.appliedAdvancedFilterGroup.id
          ? undefined
          : state.appliedAdvancedFilterGroup;
      return {
        ...state,
        isAdvancedFilterGroupLoading: true,
        appliedAdvancedFilterGroup,
      };
    },
  ),
  on(
    SharedActions.deleteSelectedAdvancedFilterGroupSuccess,
    (state: SharedFilterState, {id}) => {
      const advancedFilterGroups = [...state.advancedFilterGroups].filter((item): boolean => item.id !== id) || [];
      return {
        ...state,
        advancedFilterGroups,
        isAdvancedFilterGroupLoading: false,
        selectedAdvancedFilterGroup: null,
      };
    },
  ),
  on(
    SharedActions.deleteSelectedAdvancedFilterGroupFailure,
    (state: SharedFilterState, {error}) => {
      return {
        ...state,
        isAdvancedFilterGroupLoading: false,
        errors: [...state.errors, error],
      };
    },
  ),
  on(
    SharedActions.deleteAdvancedFilterGroupFilter,
    (state: SharedFilterState, {filter}) => {
      const selectedAdvancedFilterGroup = deleteFilterOrGroupWithinAdvancedFilterGroup(getDeepCopyOfObject(state.selectedAdvancedFilterGroup), filter.id);
      selectedAdvancedFilterGroup.saved = false;
      const advancedFilterGroups = [...state.advancedFilterGroups];
      const index = advancedFilterGroups.findIndex((item: IAdvancedFilterGroup): boolean => item.id === selectedAdvancedFilterGroup.id);
      if (index > -1) {
        advancedFilterGroups[index] = {...selectedAdvancedFilterGroup};
      }
      return {
        ...state,
        advancedFilterGroups,
        selectedAdvancedFilterGroup,
      };
    },
  ),
  on(
    SharedActions.deleteAdvancedFilterGroupGroup,
    (state: SharedFilterState, {filterGroup}) => {
      let selectedAdvancedFilterGroup = getDeepCopyOfObject(state.selectedAdvancedFilterGroup);
      if (filterGroup.id === state.selectedAdvancedFilterGroup.filterGroup.id) {
        return state;
      }
      selectedAdvancedFilterGroup = deleteFilterOrGroupWithinAdvancedFilterGroup(selectedAdvancedFilterGroup, filterGroup.id);
      const advancedFilterGroups = [...state.advancedFilterGroups];
      const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === selectedAdvancedFilterGroup.id);
      if (index > -1) {
        selectedAdvancedFilterGroup.saved = false;
        advancedFilterGroups[index] = {...selectedAdvancedFilterGroup};
      }
      return {
        ...state,
        advancedFilterIDCounter: state.advancedFilterIDCounter + 1,
        advancedFilterGroups,
        selectedAdvancedFilterGroup,
      };
    },
  ),
  on(
    SharedActions.getAdvancedFilterGroupById,
    (state: SharedFilterState) => {
      return {
        ...state,
        isAdvancedFilterGroupLoading: true,
      };
    },
  ),
  on(
    SharedActions.getAdvancedFilterGroupByIdSuccess,
    (state: SharedFilterState, {advancedFilterGroup}) => {
      const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
      const migratedGroup = migrateAdvancedFilterGroupStructure(advancedFilterGroup);
      const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === migratedGroup.id);
      if (index > -1) {
        advancedFilterGroups[index] = migratedGroup;
      }
      return {
        ...state,
        advancedFilterGroups,
        isAdvancedFilterGroupLoading: false,
        selectedAdvancedFilterGroup: index > -1 ? advancedFilterGroups[index] : migrateAdvancedFilterGroupStructure(advancedFilterGroup),
      };
    },
  ),
  on(
    SharedActions.getAdvancedFilterGroupByIdFailure,
    (state: SharedFilterState, {error}) => {
      return {
        ...state,
        errors: [...state.errors, error],
        isAdvancedFilterGroupLoading: false,
        selectedAdvancedFilterGroup: null,
      };
    },
  ),
  on(
    SharedActions.getAdvancedFilterGroups,
    (state: SharedFilterState) => {
      return {
        ...state,
        advancedFilterIDCounter: (new Date()).getTime(),
        advancedFilterGroups: [],
        selectedAdvancedFilterGroup: undefined,
        isAdvancedFilterGroupLoading: true,
      };
    },
  ),
  on(
    SharedActions.getAdvancedFilterGroupsSuccess,
    (state: SharedFilterState, {advancedFilterGroups}) => {
      const migratedGroups = (advancedFilterGroups || []).map(
        (group: IAdvancedFilterGroup): IAdvancedFilterGroup => migrateAdvancedFilterGroupStructure(group),
      );
      const appliedId = state.appliedAdvancedFilterGroup?.id;
      const selectedFromApplied = appliedId == null
        ? null
        : migratedGroups.find((group: IAdvancedFilterGroup): boolean =>
          group.id === appliedId
          && matchesAdvancedFilterGridCategory(group.grid, state.selectedAdvancedFilterGroupGrid),
        ) ?? null;
      return {
        ...state,
        advancedFilterGroups: migratedGroups,
        isAdvancedFilterGroupLoading: false,
        selectedAdvancedFilterGroup: selectedFromApplied,
      };
    },
  ),
  on(
    SharedActions.getAdvancedFilterGroupsFailure,
    (state: SharedFilterState, {error}) => {
      const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
      const foundAdvancedFilterGroup = advancedFilterGroups.find((afg: IAdvancedFilterGroup): boolean => afg.id === state.selectedAdvancedFilterGroup?.id);
      if (foundAdvancedFilterGroup) {
        foundAdvancedFilterGroup.saved = false;
      }
      return {
        ...state,
        advancedFilterGroups,
        errors: [...state.errors, error],
        isAdvancedFilterGroupLoading: false,
        selectedAdvancedFilterGroup: foundAdvancedFilterGroup ?? null,
      };
    },
  ),
  on(
    SharedActions.saveSelectedAdvancedFilterGroup,
    (state: SharedFilterState) => {
      return {
        ...state,
        isAdvancedFilterGroupLoading: true,
      };
    },
  ),
  on(
    SharedActions.saveSelectedAdvancedFilterGroupSuccess,
    (state: SharedFilterState) => {
      const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
      const selectedId = state.selectedAdvancedFilterGroup?.id;
      const foundAdvancedFilterGroup = advancedFilterGroups.find(
        (afg: IAdvancedFilterGroup): boolean => afg.id === selectedId,
      );
      if (foundAdvancedFilterGroup) {
        foundAdvancedFilterGroup.saved = true;
        foundAdvancedFilterGroup.lastModified = new Date(new Date().setMonth(new Date().getMonth() + 1));
      }
      let appliedAdvancedFilterGroup = state.appliedAdvancedFilterGroup
        ? getDeepCopyOfObject(state.appliedAdvancedFilterGroup)
        : undefined;
      if (
        appliedAdvancedFilterGroup
        && foundAdvancedFilterGroup
        && foundAdvancedFilterGroup.id === appliedAdvancedFilterGroup.id
      ) {
        appliedAdvancedFilterGroup = foundAdvancedFilterGroup;
      }
      return {
        ...state,
        advancedFilterGroups,
        appliedAdvancedFilterGroup,
        isAdvancedFilterGroupLoading: true,
        selectedAdvancedFilterGroup: foundAdvancedFilterGroup ?? state.selectedAdvancedFilterGroup,
      };
    },
  ),
  on(
    SharedActions.saveSelectedAdvancedFilterGroupFailure,
    (state: SharedFilterState, {error}) => {
      return {
        ...state,
        isAdvancedFilterGroupLoading: false,
        errors: [...state.errors, error],
      };
    },
  ),
  on(
    SharedActions.saveAdvancedFilterGroup,
    (state: SharedFilterState, {advancedFilterGroup}) => {
      const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
      const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === advancedFilterGroup.id);
      if (index > -1) {
        advancedFilterGroups[index] = {
          ...advancedFilterGroup,
          saved: false,
          lastModified: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        };
      }
      return {
        ...state,
        advancedFilterGroups,
        selectedAdvancedFilterGroup: index > -1 ? advancedFilterGroups[index] : undefined,
      };
    },
  ),
  on(
    SharedActions.setSelectedAdvancedFilterGroup,
    (state: SharedFilterState, {advancedFilterGroup}) => {
      if (!advancedFilterGroup) {
        return {
          ...state,
          selectedAdvancedFilterGroup: null,
        };
      }
      const advancedFilterGroups = getDeepCopyOfObject(state.advancedFilterGroups);
      const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === advancedFilterGroup.id);
      if (index > -1) {
        const migratedGroup = migrateAdvancedFilterGroupStructure(advancedFilterGroups[index]);
        advancedFilterGroups[index] = migratedGroup;
        return {
          ...state,
          advancedFilterGroups,
          selectedAdvancedFilterGroup: migratedGroup,
        };
      }
      const migratedGroup = migrateAdvancedFilterGroupStructure(advancedFilterGroup);
      return {
        ...state,
        selectedAdvancedFilterGroup: migratedGroup,
      };
    },
  ),
  on(
    SharedActions.setSelectedAdvancedFilterGroupOperator,
    (state: SharedFilterState, {operator, filterGroupId}) =>
      onSetSelectedAdvancedFilterGroupOperator(state, filterGroupId, operator),
  ),
  on(
    SharedActions.setAdvancedFilterGroupGrid,
    (state: SharedFilterState, {grid}) => {
      return {
        ...state,
        selectedAdvancedFilterGroupGrid: grid,
      };
    },
  ),
  on(
    SharedActions.setFilterAfterValueChange,
    (state: SharedFilterState, {filter}) => {
      const selectedAdvancedFilterGroup = findAndReplaceFilterWithinAdvancedFilterGroup(getDeepCopyOfObject(state.selectedAdvancedFilterGroup), filter);
      // selectedAdvancedFilterGroup.saved = false;
      selectedAdvancedFilterGroup.lastModified = new Date();
      selectedAdvancedFilterGroup.saved = false;
      const advancedFilterGroups = [...state.advancedFilterGroups];
      const index = advancedFilterGroups.findIndex((afg: IAdvancedFilterGroup): boolean => afg.id === selectedAdvancedFilterGroup.id);
      advancedFilterGroups[index] = selectedAdvancedFilterGroup;
      return {
        ...state,
        advancedFilterGroups,
        selectedAdvancedFilterGroup,
      };
    },
  ),
);

export const sharedFilterReducer = (state: SharedFilterState, action: Action): SharedFilterState => createSharedFilterReducer(state, action);
